import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { IndexType } from "./IndexType";

import { BaseTableDefinitionFactory } from "./BaseTableDefinitionFactory";
import { TableDefinition, BfxColDef } from "./TableDefinition";
import { FilterOrderTypes } from "./FilterTypeEnums";
import { ColumnFilterType } from "./ColumnFilterType";

import { BlotterType, AssetClass } from "shared/cubiq-model-multi-asset";
import { PrivatizationHelper } from "./orders/PrivatizationHelper";

import { BfxConfigIDRefHelper } from "shared/helper-services/bfx-config-id-ref-helper";

import { CcyPairLookupService } from "../lookup/model/impl/CcyPairLookupService";
import { ClientLookupService } from "../lookup/model/impl/ClientLookupService";
import { AccountLookupService } from "../lookup/model/impl/AccountLookupService";
import { TraderGroupLookupService } from "../lookup/model/impl/TraderGroupLookupService";
import { SalesGroupLookupService } from "../lookup/model/impl/SalesGroupLookupService";
import { UserLookupService } from "../lookup/model/impl/UserLookupService";
import { SystemConfigPromiseService } from "shared/auth-services";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";
import { CustomFieldLookupService } from "../lookup/model/impl/CustomFieldLookupService";

import { SysFeatures } from "../model/core/SysFeatures";

import { forwardRef, Injectable, Optional } from "@angular/core";
import {
  COLUMN_CLIENT_TRADER,
  COLUMN_DESK_ID,
  COLUMN_INSTRUMENT,
  COLUMN_ORDER_FIXING_SOURCE_CODE,
  COLUMN_ORDER_PRODUCT,
  // COLUMN_CLIENT_CODE,
  COLUMN_ORDER_TYPE,
  COLUMN_SALES_GROUP_ID,
  COLUMN_VALUE_DATE,
  RENDERER_ORDER_TYPE_FUNC,
} from "./OrdersTableDefinitionFactory.constants";
import {
  COLUMN_INTER_INSTRUMENT,
  COLUMN_INTER_DESK_ID,
  COLUMN_INTER_ORDER_PRODUCT,
} from "./InterventionTableDefinitionFactory.constants";
import { TranslateService } from "@ngx-translate/core";
import { AlgoStrategyLookupService } from "../lookup/model/impl/AlgoStrategyLookupService";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TRADE_TABLE_DEFINITION_VER } from "./TableDefinitionFactory.constants";
import { OrderTypeHelper } from "./orders/OrderTypeHelper";
import { ITooltipParams, ValueFormatterParams, ValueGetterParams } from "ag-grid-community";
import { ClientModelPrivatization } from "./privatization/ClientModelPrivatization";
import { AccountModelPrivatization } from "./privatization/AccountModelPrivatization";
import { ClientTraderModelPrivatization } from "./privatization/ClientTraderModelPrivatization";

const SL_ORDER_TYPE: string = "SL";
const DATE_LOCALE_FORMAT: string = "DD-MMM-YY HH:mm:ss";
const DATE_FIXING_FORMAT: string = "DD-MMM-YYYY";
const DEFAULT_CELL_CLASS: string = "ag-cell-default";
// OMS-30890 Per Keith D, "-" is reserved for empty numerical fields.
import {
  EMPTY_AMT_RATE_VALUE,
  EMPTY_TEXT_VALUE,
} from "shared/cubiq-services/model/BaseTableDefinitionFactory";
import { AtdlTooltipUiRep } from "./adtl/atdl-tooltip-ui-rep";

import { BMARK_DEFAULT_NAME } from "./orders/OrderTransactionHelper";
import { BfxSysConfigUtilService } from "shared/auth-services";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import { Inject } from "@angular/core";
import {
  COLUMN_ACCOUNT_CODE,
  COLUMN_ACCOUNT_NAME,
  COLUMN_BACK_OFFICE_IDS,
  COLUMN_CHANNEL_CODE,
  COLUMN_CLIENT_ALLIN,
  COLUMN_CLIENT_BRANCH,
  COLUMN_CLIENT_CODE,
  COLUMN_CLIENT_COMMENT,
  COLUMN_CLIENT_NAME,
  COLUMN_CLIENT_PTS,
  COLUMN_CLIENT_SPOT,
  COLUMN_CLORDERID,
  COLUMN_CONFIRMED_BY,
  COLUMN_COUNTER_AMT,
  COLUMN_COUNTER_CURRENCY,
  COLUMN_DEAL_ID,
  COLUMN_DEAL_STATE,
  COLUMN_DEALT_AMT,
  COLUMN_DEALT_AMT_USD,
  COLUMN_DEALT_CURRENCY,
  COLUMN_DIR,
  COLUMN_ENTRY_DATE,
  COLUMN_ENTRY_USER_ID,
  COLUMN_FILL_COMMENT,
  COLUMN_FILL_DATE,
  COLUMN_FILL_STATE,
  COLUMN_FILL_USER_CODE,
  COLUMN_FILL_USER_NAME,
  COLUMN_FIXING_DATE,
  COLUMN_FWD_PORTFOLIO,
  COLUMN_LAST_MARKET,
  COLUMN_MARKET_ALLIN,
  COLUMN_MARKET_PTS,
  COLUMN_MARKET_SPOT,
  COLUMN_MARKUP,
  COLUMN_OBO_ACCOUNT,
  COLUMN_OBO_CLIENT,
  COLUMN_OBO_ENTRY_USER,
  COLUMN_ORDER_ID,
  COLUMN_PRIVATE_COMMENT,
  COLUMN_REQUEST_RATE,
  COLUMN_SPOT_PORTFOLIO,
  COLUMN_STRATEGY_ID,
  COLUMN_TRADE_CLIENT_TRADER,
  COLUMN_TRADE_DATE,
  COLUMN_TRADE_DESK_ID,
  COLUMN_TRADE_FIXING_SOURCE_CODE,
  COLUMN_TRADE_ORDER_PRODUCT,
  COLUMN_TRADE_ORDER_TYPE,
  COLUMN_TRADE_SALES_GROUP_ID,
  COLUMN_TRADE_VALUE_DATE,
} from "./TradeTableDefinitionFactory.constants";
import { TradeStatusIconHelper } from "./orders/TradeStatusIconHelper";
import { CcyLookupService } from "shared/cubiq-services/lookup/model/impl/CcyLookupService";

@Injectable()
export class TradeTableDefinitionFactory extends BaseTableDefinitionFactory {
  protected defaultSortModel: any = [{ colId: "creationDate", sort: "desc" }];
  private myClientTraderEnabled: boolean;
  private myPrivateCommentsEnabled: boolean;
  private myMarkupEnabled: boolean;
  protected myPartFillDisabledTypeText: string = "";
  protected myNdfEnabled: boolean;
  protected myFwdEnabled: boolean;
  public myOrderTypeHelper: OrderTypeHelper;
  public statusIconHelper: TradeStatusIconHelper;

  protected privatizationHelper: PrivatizationHelper;
  private passThroughParamsEnabled: boolean = true;
  public bmarkShortNm: string;
  protected enabledOBO: boolean = false;
  protected enablePortfolio: boolean = false;

  constructor(
    profileService: ProfilePromiseActionService,
    protected atdlTooltipUiRep: AtdlTooltipUiRep,
    // It is not possible to inject against an interface
    @Optional() private _salesGroupLookup: SalesGroupLookupService,
    @Optional() protected _traderGroupLookup: TraderGroupLookupService,
    @Optional() private _userLookup: UserLookupService,
    @Optional() private _sysConfigService: SystemConfigPromiseService,
    @Optional() protected _translateService: TranslateService,
    @Optional() protected algoStrategyLookupService: AlgoStrategyLookupService,
    @Optional() protected ccyPairLookupService: CcyPairLookupService,
    protected ccyLookupService: CcyLookupService,
    @Optional() protected _clientLookup: ClientLookupService,
    @Optional() protected _accountLookup: AccountLookupService,
    @Optional() protected cmp: ClientModelPrivatization,
    @Optional() protected amp: AccountModelPrivatization,
    @Optional() protected ctmp: ClientTraderModelPrivatization,
    @Optional() protected sysConfigUtil: BfxSysConfigUtilService,
    @Optional() @Inject(forwardRef(() => BfxConfigService)) configService,
    @Optional() protected customFieldLookupService: CustomFieldLookupService,
  ) {
    super(profileService);

    this.myOrderTypeHelper = new OrderTypeHelper(
      this.myPartFillDisabledTypeText,
      this.algoStrategyLookupService,
      this.sysConfigUtil,
      "requestOrderType",
    );

    this.privatizationHelper = new PrivatizationHelper(_clientLookup, _accountLookup);
    this.statusIconHelper = new TradeStatusIconHelper();

    configService.getUIConfig().then((config) => {
      this.enabledOBO = config && config.general ? config.general.oboEnabled : false;
    });

    this.getBmarkOrderDisplayName();
  }

  // this override for system configs applicable for blotter definition
  protected getTableCongiPromises(): Array<Promise<any>> {
    return [
      this._sysConfigService.getSystemConfig(SysFeatures.clientTraderUsersEnabled), // 0
      this._sysConfigService.getSystemConfig(SysFeatures.featuresEnablePrivateComments), // 1
      this._sysConfigService.getSystemConfig(SysFeatures.markupEnabled), // 2
      this._sysConfigService.getSystemConfig(SysFeatures.ndfEnabled), // 3
      this._sysConfigService.getSystemConfig(SysFeatures.fwdEnabled), // 4
      this._sysConfigService.getSystemConfig(SysFeatures.partFillDisabledTypeText), // 5
      this._sysConfigService.getSystemConfig(SysFeatures.displayUserNames), // 6
      this._sysConfigService.getSystemConfig(SysFeatures.passThroughColumEnabled), // 7
    ];
  }

  // this override to set flags returned by promises
  protected handleTableCongiPromises(values: Array<any>) {
    this.myClientTraderEnabled = this.valueOn(values[0]);
    this.myPrivateCommentsEnabled = this.valueOn(values[1]);
    this.myMarkupEnabled = this.valueOn(values[2]);
    this.myNdfEnabled = this.valueOn(values[3]);
    this.myFwdEnabled = this.valueOn(values[4]);
    this.myPartFillDisabledTypeText = values[5] != null ? values[5] : "";
    //this.displayUserNames = values[6]; Disabled to avoid confusion, please use inherited showUserName
    this.passThroughParamsEnabled = this.valueOn(values[7]);

    this.myOrderTypeHelper = new OrderTypeHelper(
      this.myPartFillDisabledTypeText,
      this.algoStrategyLookupService,
      this.sysConfigUtil,
      "requestOrderType",
    );
  }

  protected initColdDef(): any {
    return { suppressMenu: true, cellClass: [DEFAULT_CELL_CLASS] };
  }

  public createDef(): TableDefinition {
    super.createDef();
    let fwdColsVisibleByDefault: boolean = this.myNdfEnabled || this.myFwdEnabled;

    let td = new TableDefinition("id", "as_sort_id");
    td.clientTradeTableDefinition = true;

    // requestSubmissionTime's column
    td.setDefaultSort(1, "desc");

    let selectionColumn: BfxColDef = this.initColdDef();
    selectionColumn.field = "selection";
    selectionColumn.sortingOrder = [];
    selectionColumn.pinned = "left";
    selectionColumn.lockPosition = true;
    selectionColumn.checkboxSelection = true;
    selectionColumn.headerName = "(0)";
    selectionColumn.headerCheckboxSelection = true;
    selectionColumn.headerCheckboxSelectionFilteredOnly = true;
    selectionColumn.suppressSorting = true;
    selectionColumn.suppressMovable = true;
    selectionColumn.suppressResize = false;
    selectionColumn.suppressMenu = true;
    // enforced width on fixed column
    selectionColumn.cellClass = ["status-icon-cell"];
    selectionColumn.width = this.fourWs;
    selectionColumn.minWidth = this.fourWs;
    selectionColumn.maxWidth = this.fourWs;
    selectionColumn.position = this.position.next();
    selectionColumn.omitFromPicker = true;
    selectionColumn.disableInPicker = true;
    selectionColumn.visibleInEmbeddedMode = true;
    selectionColumn.indexType = IndexType.None;
    selectionColumn.columnFilterType = ColumnFilterType.NONE;
    selectionColumn.valueGetter = (params) => {
      const detailsBlotter = params.context != null && params.context.embeddedTableMode === true;
      return detailsBlotter ? "" : this.statusIconHelper.getIcons(params.data);
    };
    selectionColumn.cellRenderer = function (params) {
      return params.value != null ? params.value : "";
    };
    selectionColumn.tooltipValueGetter = function (this: null) {
      return "";
    };

    td.addColumn(selectionColumn);

    // Fill Date
    let fillDateColumn: BfxColDef = this.initColdDef();
    fillDateColumn.minWidth = this.oneWs;
    fillDateColumn.width = this.eightWs;
    fillDateColumn.headerName = "Fill Date";
    fillDateColumn.field = COLUMN_FILL_DATE;
    fillDateColumn.valueFormatter = this.formatDate.bind(this, DATE_LOCALE_FORMAT);
    fillDateColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(fillDateColumn)) {
      fillDateColumn.position = this.position.next();
    }
    td.addColumn(fillDateColumn);

    // Deal State
    let dealStateColumn: BfxColDef = this.initColdDef();
    dealStateColumn.minWidth = this.oneWs;
    dealStateColumn.width = this.sevenWs;
    dealStateColumn.headerName = "Deal State";
    dealStateColumn.field = COLUMN_DEAL_STATE;
    dealStateColumn.columnFilterType = ColumnFilterType.ENUM;
    dealStateColumn.columnFilterData = CubiqTS.OrderDealState;
    dealStateColumn.getFilterValue = (dataEntity: CubiqTS.DealImpl) => {
      return CubiqTS.OrderDealState[dataEntity.dealStateId];
    };

    if (!this.restoreFromConfig(dealStateColumn)) {
      dealStateColumn.position = this.position.next();
    }
    dealStateColumn.valueGetter = (parameters) => {
      let status: number = -1;

      if (parameters.data["dealStateId"] >= 0) {
        status = parameters.data["dealStateId"];
      }

      // if (parameters && CubiqTS.OrderType.LIMIT_CALL === item["orderType"]) {
      //     // CALL filles have no deal state
      //     return "-";
      // }

      return this._translateService.instant("OrderDealState." + CubiqTS.OrderDealState[status]);
    };
    td.addColumn(dealStateColumn);

    // Fill User = Username
    let fillUsernameColumn: BfxColDef = this.initColdDef();
    fillUsernameColumn.minWidth = this.oneWs;
    fillUsernameColumn.width = this.sixWs;
    fillUsernameColumn.headerName = "Fill User";
    fillUsernameColumn.field = this.showUserName ? COLUMN_FILL_USER_NAME : COLUMN_FILL_USER_CODE;
    fillUsernameColumn.valueGetter = (params: ValueGetterParams) => {
      if (params.data instanceof CubiqTS.DealImpl) {
        let deal: CubiqTS.DealImpl = params.data;
        return deal.fillUserCode;
      }
      return params.data != null ? params.data["fillUserCode"] : null;
    };
    fillUsernameColumn.valueFormatter = (params: ValueFormatterParams) => {
      if (params.data instanceof CubiqTS.DealImpl) {
        let deal: CubiqTS.DealImpl = params.data;
        return this.showUserName ? deal.fillUserName : deal.fillUserCode;
      }
      return params.value != null ? params.value : "-";
    };
    fillUsernameColumn.columnFilterType = ColumnFilterType.USER;
    if (!this.restoreFromConfig(fillUsernameColumn)) {
      fillUsernameColumn.position = this.position.next();
    }
    td.addColumn(fillUsernameColumn);

    // Order Channel
    let channelCdColumn: BfxColDef = this.initColdDef();
    channelCdColumn.headerName = "Channel";
    channelCdColumn.field = COLUMN_CHANNEL_CODE;
    channelCdColumn.minWidth = this.oneWs;
    channelCdColumn.width = this.nineWs;
    channelCdColumn.showTooltip = true;
    channelCdColumn.columnFilterType = ColumnFilterType.CHANNEL;
    if (!this.restoreFromConfig(channelCdColumn)) {
      channelCdColumn.hide = true;
      channelCdColumn.position = this.position.next();
    }
    td.addColumn(channelCdColumn);

    // client id
    let clientId: BfxColDef = this.initColdDef();
    clientId.headerName = "Client ID";
    clientId.field = COLUMN_CLIENT_CODE;
    clientId.indexType = IndexType.String;
    clientId.minWidth = this.oneWs;
    clientId.width = this.tenWs;
    clientId.columnFilterType = ColumnFilterType.CLIENTID;
    clientId.valueGetter = function (params) {
      return this.privatizationHelper.getPrivatizedValue(params, COLUMN_CLIENT_CODE);
    }.bind(this);
    if (!this.restoreFromConfig(clientId)) {
      clientId.hide = true;
      clientId.position = this.position.next();
    }
    td.addColumn(clientId);

    // client name
    let clNm: BfxColDef = this.initColdDef();
    clNm.headerName = "Client Name";
    clNm.field = COLUMN_CLIENT_NAME;
    clNm.indexType = IndexType.String;
    clNm.minWidth = this.oneWs;
    clNm.width = this.tenWs;
    clNm.columnFilterType = ColumnFilterType.CLIENT;
    clNm.valueGetter = function (params) {
      return this.privatizationHelper.getPrivatizedValue(params, "clientName");
    }.bind(this);
    clNm.getFilterValue = function (params) {
      return this.privatizationHelper.getPrivatizedValue({ data: params }, COLUMN_CLIENT_CODE);
    }.bind(this);
    clNm.cellClass = ["blotter_left_align_col"];
    if (!this.restoreFromConfig(clNm)) {
      clNm.position = this.position.next();
    }
    td.addColumn(clNm);

    // account id
    let accId: BfxColDef = this.initColdDef();
    accId.headerName = "Account ID";
    accId.field = COLUMN_ACCOUNT_CODE;
    accId.indexType = IndexType.String;
    accId.minWidth = this.oneWs;
    accId.width = this.tenWs;
    accId.columnFilterType = ColumnFilterType.ACCOUNTID;
    accId.valueGetter = function (params) {
      return this.privatizationHelper.getPrivatizedValue(params, "accountCode");
    }.bind(this);
    if (!this.restoreFromConfig(accId)) {
      accId.hide = true;
      accId.position = this.position.next();
    }
    td.addColumn(accId);

    // Account Name
    let accountNameColumn: BfxColDef = this.initColdDef();
    accountNameColumn.headerName = "Account Name";
    accountNameColumn.field = COLUMN_ACCOUNT_NAME;
    accountNameColumn.minWidth = this.oneWs;
    accountNameColumn.width = this.tenWs;
    accountNameColumn.columnFilterType = ColumnFilterType.ACCOUNT;
    accountNameColumn.valueGetter = function (params) {
      return this.privatizationHelper.getPrivatizedValue(params, "accountName");
    }.bind(this);
    accountNameColumn.getFilterValue = ((dataEntity: any) => {
      return this.privatizationHelper.getPrivatizedValue({ data: dataEntity }, "accountCode");
    }).bind(this);
    if (!this.restoreFromConfig(accountNameColumn)) {
      // accountNameColumn.hide = true;
      accountNameColumn.position = this.position.next();
    }
    td.addColumn(accountNameColumn);

    // Instrument
    let instrumentColumn: BfxColDef = this.initColdDef();
    instrumentColumn.headerName = "Instrument";
    instrumentColumn.field = COLUMN_INSTRUMENT;
    instrumentColumn.indexType = IndexType.String;
    instrumentColumn.minWidth = this.oneWs;
    instrumentColumn.width = this.sixWs;
    instrumentColumn.getFilterValue = (deal: CubiqTS.Deal) => {
      if (deal.isInvert) {
        return deal.nonBaseCcyCd + "/" + deal.baseCcyCd;
      }
      return deal.baseCcyCd + "/" + deal.nonBaseCcyCd;
    };
    instrumentColumn.valueGetter = (params): string => {
      const deal = params.data as CubiqTS.Deal;
      if (deal) {
        if (deal.isInvert) {
          return deal.nonBaseCcyCd + "/" + deal.baseCcyCd;
        }
        return deal.baseCcyCd + "/" + deal.nonBaseCcyCd;
      }
      return "";
    };
    instrumentColumn.columnFilterType = ColumnFilterType.CURRENCY_PAIR;
    if (!this.restoreFromConfig(instrumentColumn)) {
      instrumentColumn.position = this.position.next();
    }
    td.addColumn(instrumentColumn);

    // Direction: Buy or Sell
    let dirColumn: BfxColDef = this.initColdDef();
    dirColumn.field = COLUMN_DIR;
    dirColumn.headerName = "Dir";
    dirColumn.minWidth = this.twoWs;
    dirColumn.width = this.threeWs;
    dirColumn.cellRenderer = "directionRenderer";
    dirColumn.cellClass = ["blotter_center_align_col intent_col"];
    dirColumn.columnFilterType = ColumnFilterType.NONE;
    dirColumn.columnFilterData = CubiqTS.OrderIntent;
    if (!this.restoreFromConfig(dirColumn)) {
      dirColumn.position = this.position.next();
    }
    dirColumn.tooltipValueGetter = (params) => {
      if (CubiqTS.OrderType.LIMIT_CALL === params.data.orderType) {
        return "CALL";
      }
      return params.data.isBuy ? "Buy" : "Sell";
    };
    dirColumn.comparator = (value1, value2, node1, node2) => {
      const call1 = node1.data.orderType === CubiqTS.OrderType.LIMIT_CALL;
      const call2 = node2.data.orderType === CubiqTS.OrderType.LIMIT_CALL;
      const label1 = call1 ? "Call" : value1 ? "Buy" : "Sell";
      const label2 = call2 ? "Call" : value2 ? "Buy" : "Sell";
      return label1.localeCompare(label2);
    };

    td.addColumn(dirColumn);

    // Dealt ccy
    let dealtCcy: BfxColDef = this.initColdDef();
    dealtCcy.headerName = "Dealt Ccy";
    dealtCcy.field = COLUMN_DEALT_CURRENCY;
    dealtCcy.minWidth = this.oneWs;
    dealtCcy.width = this.fiveWs;
    dealtCcy.cellClass = "blotter_center_align_col";
    dealtCcy.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(dealtCcy)) {
      dealtCcy.position = this.position.next();
    }
    td.addColumn(dealtCcy);

    // Dealt Amount = Fill Amount
    let fillAmtColumn: BfxColDef = this.initColdDef();
    fillAmtColumn.headerName = "Dealt Amt";
    fillAmtColumn.field = COLUMN_DEALT_AMT;
    fillAmtColumn.indexType = IndexType.NaturalString;
    fillAmtColumn.minWidth = this.sixWs;
    fillAmtColumn.width = this.eightWs;
    fillAmtColumn.columnFilterType = ColumnFilterType.NUMBER;
    if (!this.restoreFromConfig(fillAmtColumn)) {
      fillAmtColumn.position = this.position.next();
    }
    fillAmtColumn.cellClass = "blotter_amt_align";
    fillAmtColumn.valueFormatter = (params) => {
      let deal: CubiqTS.DealImpl = params.data;
      const amountString = this.formatCcyAmount(deal.dealtCcy, params);
      return amountString;
    };
    td.addColumn(fillAmtColumn);

    // Dealt Amount (USD) = Fill amt. in usd equivalent
    let usdAmtColumn: BfxColDef = this.initColdDef();
    usdAmtColumn.headerName = "Dealt Amt (USD)";
    usdAmtColumn.field = COLUMN_DEALT_AMT_USD;
    usdAmtColumn.indexType = IndexType.NaturalString;
    usdAmtColumn.minWidth = this.oneWs;
    usdAmtColumn.width = this.eightWs;
    usdAmtColumn.valueFormatter = (params) => {
      const amountString = this.formatCcyAmount("USD", params);
      return amountString;
    };
    usdAmtColumn.cellClass = "blotter_amt_align";
    usdAmtColumn.columnFilterType = ColumnFilterType.NUMBER;
    if (!this.restoreFromConfig(usdAmtColumn)) {
      //usdAmtColumn.hide = true;
      usdAmtColumn.position = this.position.next();
    }
    td.addColumn(usdAmtColumn);

    // order type
    let orderTypeColumn: BfxColDef = this.initColdDef();
    orderTypeColumn.headerName = "Order Type";
    orderTypeColumn.field = COLUMN_TRADE_ORDER_TYPE;
    orderTypeColumn.cellRenderer = RENDERER_ORDER_TYPE_FUNC;
    orderTypeColumn.indexType = IndexType.String;
    orderTypeColumn.alias = [COLUMN_ORDER_TYPE];
    orderTypeColumn.minWidth = this.oneWs;
    orderTypeColumn.width = this.sixWs;
    orderTypeColumn.cellClass = ["blotter_center_align_col"];
    orderTypeColumn.columnFilterType = ColumnFilterType.ORDERTYPE;
    orderTypeColumn.columnFilterData = FilterOrderTypes;
    orderTypeColumn.valueGetter = (params) => {
      return this.myOrderTypeHelper.getType(params.data);
    };
    orderTypeColumn.getFilterValue = (data) => {
      return this.myOrderTypeHelper.getFilterOrderType(data);
    };

    if (!this.restoreFromConfig(orderTypeColumn)) {
      orderTypeColumn.position = this.position.next();
    }

    td.addColumn(orderTypeColumn);

    // product: spot/fwd/fwd off/ndf etc
    let productColumn: BfxColDef = this.initColdDef();
    productColumn.headerName = "Product";
    productColumn.field = COLUMN_TRADE_ORDER_PRODUCT;
    productColumn.alias = [COLUMN_ORDER_PRODUCT, COLUMN_INTER_ORDER_PRODUCT];
    productColumn.minWidth = this.oneWs;
    productColumn.width = this.fiveWs;
    productColumn.columnFilterType = ColumnFilterType.ENUM;
    productColumn.columnFilterData = CubiqTS.Product;
    if (!this.restoreFromConfig(productColumn)) {
      productColumn.position = this.position.next();
    }
    td.addColumn(productColumn);

    // market spot
    let marketSpotColumn: BfxColDef = this.initColdDef();
    marketSpotColumn.headerName = "Market Spot";
    marketSpotColumn.field = COLUMN_MARKET_SPOT;
    marketSpotColumn.minWidth = this.oneWs;
    marketSpotColumn.width = this.sevenWs;
    marketSpotColumn.cellClass = "blotter_amt_align";
    marketSpotColumn.columnFilterType = ColumnFilterType.NONE;
    marketSpotColumn.valueFormatter = this.formatRate.bind(this);
    if (!this.restoreFromConfig(marketSpotColumn)) {
      marketSpotColumn.position = this.position.next();
    }
    td.addColumn(marketSpotColumn);

    // market forward points // fix with ccy
    let marketForwardPointsColumn: BfxColDef = this.initColdDef();
    marketForwardPointsColumn.headerName = "Market Pts";
    marketForwardPointsColumn.field = COLUMN_MARKET_PTS;
    marketForwardPointsColumn.minWidth = this.oneWs;
    marketForwardPointsColumn.width = this.sixWs;
    marketForwardPointsColumn.cellClass = "blotter_amt_align";
    marketForwardPointsColumn.columnFilterType = ColumnFilterType.NONE;
    marketForwardPointsColumn.valueFormatter = this.formatPoints.bind(this);

    if (!this.restoreFromConfig(marketForwardPointsColumn)) {
      marketForwardPointsColumn.hide = !fwdColsVisibleByDefault;
      marketForwardPointsColumn.position = this.position.next();
    }
    td.addColumn(marketForwardPointsColumn);

    // market forward rate
    let marketForwardRateColumn: BfxColDef = this.initColdDef();
    marketForwardRateColumn.headerName = "Market All-In";
    marketForwardRateColumn.field = COLUMN_MARKET_ALLIN;
    marketForwardRateColumn.minWidth = this.oneWs;
    marketForwardRateColumn.width = this.sevenWs;
    marketForwardRateColumn.cellClass = "blotter_amt_align";
    marketForwardRateColumn.columnFilterType = ColumnFilterType.NONE;
    marketForwardRateColumn.valueFormatter = this.formatRate.bind(this);
    if (!this.restoreFromConfig(marketForwardRateColumn)) {
      marketForwardRateColumn.hide = !fwdColsVisibleByDefault;
      marketForwardRateColumn.position = this.position.next();
    }
    td.addColumn(marketForwardRateColumn);

    // client spot
    let clientSpotColumn: BfxColDef = this.initColdDef();
    clientSpotColumn.headerName = "Client Spot";
    clientSpotColumn.field = COLUMN_CLIENT_SPOT;
    clientSpotColumn.minWidth = this.oneWs;
    clientSpotColumn.width = this.sevenWs;
    clientSpotColumn.cellClass = "blotter_amt_align";
    clientSpotColumn.columnFilterType = ColumnFilterType.NONE;
    clientSpotColumn.valueFormatter = this.formatRate.bind(this);
    if (!this.restoreFromConfig(clientSpotColumn)) {
      clientSpotColumn.position = this.position.next();
    }
    td.addColumn(clientSpotColumn);

    // clientPts
    let clientPtsColumn: BfxColDef = this.initColdDef();
    clientPtsColumn.headerName = "Client Pts";
    clientPtsColumn.field = COLUMN_CLIENT_PTS;
    clientPtsColumn.minWidth = this.oneWs;
    clientPtsColumn.width = this.sixWs;
    clientPtsColumn.cellClass = "blotter_amt_align";
    clientPtsColumn.columnFilterType = ColumnFilterType.NONE;
    clientPtsColumn.valueFormatter = this.formatPoints.bind(this);
    if (!this.restoreFromConfig(clientPtsColumn)) {
      clientPtsColumn.hide = !fwdColsVisibleByDefault;
      clientPtsColumn.position = this.position.next();
    }

    td.addColumn(clientPtsColumn);

    // Client All-In (market forward rate)
    let clientForwardRateColumn: BfxColDef = this.initColdDef();

    clientForwardRateColumn.headerName = "Client All-In";
    clientForwardRateColumn.field = COLUMN_CLIENT_ALLIN;
    clientForwardRateColumn.minWidth = this.oneWs;
    clientForwardRateColumn.width = this.sevenWs;
    clientForwardRateColumn.cellClass = "blotter_amt_align";
    clientForwardRateColumn.columnFilterType = ColumnFilterType.NONE;
    clientForwardRateColumn.valueFormatter = this.formatRate.bind(this);
    if (!this.restoreFromConfig(clientForwardRateColumn)) {
      clientForwardRateColumn.hide = !fwdColsVisibleByDefault;
      clientForwardRateColumn.position = this.position.next();
    }
    td.addColumn(clientForwardRateColumn);

    if (this.myMarkupEnabled) {
      // Sales Margin
      let salesMarginColumn: BfxColDef = this.initColdDef();
      salesMarginColumn.headerName = "Markup";
      salesMarginColumn.field = COLUMN_MARKUP;
      salesMarginColumn.minWidth = this.oneWs;
      salesMarginColumn.width = this.sixWs;
      salesMarginColumn.cellClass = "blotter_amt_align";
      salesMarginColumn.columnFilterType = ColumnFilterType.NONE;
      salesMarginColumn.valueFormatter = this.formatMarkup.bind(this);
      if (!this.restoreFromConfig(salesMarginColumn)) {
        salesMarginColumn.position = this.position.next();
      }
      td.addColumn(salesMarginColumn);
    }

    // Counter ccy
    let counterCcy: BfxColDef = this.initColdDef();
    counterCcy.headerName = "Counter Ccy";
    counterCcy.field = COLUMN_COUNTER_CURRENCY;
    counterCcy.minWidth = this.oneWs;
    counterCcy.width = this.fiveWs;
    counterCcy.valueFormatter = this.formatCurrency.bind(this, true);
    counterCcy.cellClass = "blotter_center_align_col";
    counterCcy.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(counterCcy)) {
      counterCcy.position = this.position.next();
    }
    td.addColumn(counterCcy);

    // Contra amount
    let contraAmountColumn: BfxColDef = this.initColdDef();
    contraAmountColumn.field = COLUMN_COUNTER_AMT;
    contraAmountColumn.headerName = "Counter Amt";
    contraAmountColumn.minWidth = this.sixWs;
    contraAmountColumn.width = this.eightWs;
    contraAmountColumn.indexType = IndexType.NaturalString;
    contraAmountColumn.cellClass = "blotter_amt_align";
    contraAmountColumn.valueFormatter = (params) => {
      let deal: CubiqTS.DealImpl = params.data;
      const ccy =
        deal.baseCurrencyCd == deal.dealtCcy ? deal.nonBaseCurrencyCd : deal.baseCurrencyCd;
      const amountString = this.formatCcyAmount(ccy, params);
      return amountString;
    };
    contraAmountColumn.columnFilterType = ColumnFilterType.NUMBER;
    if (!this.restoreFromConfig(contraAmountColumn)) {
      contraAmountColumn.position = this.position.next();
    }
    contraAmountColumn.tooltipValueGetter = (params) => {
      let deal: CubiqTS.DealImpl = params.data;
      const ccy =
        deal.baseCurrencyCd == deal.dealtCcy ? deal.nonBaseCurrencyCd : deal.baseCurrencyCd;
      const amountString = this.formatCcyAmountTooltip(ccy, params);
      return amountString;
    };
    td.addColumn(contraAmountColumn);

    // Order rate
    let orderRateColumn: BfxColDef = this.initColdDef();
    orderRateColumn.field = COLUMN_REQUEST_RATE;
    orderRateColumn.headerName = "Request Rate";
    orderRateColumn.indexType = IndexType.NaturalString;
    orderRateColumn.minWidth = this.oneWs;
    orderRateColumn.width = this.sevenWs;
    orderRateColumn.cellClass = "blotter_center_align_numeric"; // 'blotter_amt_align';
    orderRateColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(orderRateColumn)) {
      orderRateColumn.position = this.position.next();
    }
    orderRateColumn.valueFormatter = (params: ValueFormatterParams) => {
      let ret = params.value;
      if (null == ret) {
        ret = EMPTY_AMT_RATE_VALUE;
      }
      return ret;
    };
    td.addColumn(orderRateColumn);

    // Trade Date
    let tradeDateColumn: BfxColDef = this.initColdDef();
    tradeDateColumn.field = COLUMN_TRADE_DATE;
    tradeDateColumn.headerName = "Trade Date";
    tradeDateColumn.minWidth = this.oneWs;
    tradeDateColumn.width = this.sixWs;
    tradeDateColumn.indexType = IndexType.Double;
    tradeDateColumn.valueFormatter = this.formatUTCDate.bind(this, "DD-MMM-YY");
    tradeDateColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(tradeDateColumn)) {
      tradeDateColumn.position = this.position.next();
    }
    td.addColumn(tradeDateColumn);

    // value date
    let valueDateColumn: BfxColDef = this.initColdDef();
    valueDateColumn.field = COLUMN_TRADE_VALUE_DATE;
    valueDateColumn.alias = [COLUMN_VALUE_DATE];
    valueDateColumn.headerName = "Value Date";
    valueDateColumn.minWidth = this.oneWs;
    valueDateColumn.width = this.sixWs;
    valueDateColumn.indexType = IndexType.Double;
    valueDateColumn.valueFormatter = this.formatUTCDate.bind(this, "DD-MMM-YY");
    valueDateColumn.columnFilterType = ColumnFilterType.DATE;
    if (!this.restoreFromConfig(valueDateColumn)) {
      valueDateColumn.position = this.position.next();
    }
    td.addColumn(valueDateColumn);

    // Trader Group
    let traderGroupColumn: BfxColDef = this.initColdDef();
    traderGroupColumn.field = COLUMN_TRADE_DESK_ID;
    traderGroupColumn.headerName = "Trader Group";
    traderGroupColumn.alias = [COLUMN_DESK_ID, COLUMN_INTER_DESK_ID];
    traderGroupColumn.minWidth = this.oneWs;
    traderGroupColumn.width = this.sevenWs;
    traderGroupColumn.valueGetter = this.tradeTraderGroupValueGetter.bind(this);
    traderGroupColumn.columnFilterType = ColumnFilterType.TRADERGROUP;
    if (!this.restoreFromConfig(traderGroupColumn)) {
      traderGroupColumn.position = this.position.next();
    }
    td.addColumn(traderGroupColumn);

    // Deal Id
    let tradeIdColumn: BfxColDef = this.initColdDef();
    tradeIdColumn.headerName = "Deal ID";
    tradeIdColumn.field = COLUMN_DEAL_ID;
    tradeIdColumn.minWidth = this.oneWs;
    tradeIdColumn.width = BfxConfigIDRefHelper.getDealRefWs();
    tradeIdColumn.indexType = IndexType.Integer;
    tradeIdColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(tradeIdColumn)) {
      tradeIdColumn.position = this.position.next();
    }
    tradeIdColumn.valueFormatter = BfxConfigIDRefHelper.dealRefValueFormatter;
    tradeIdColumn.getQuickFilterText = BfxConfigIDRefHelper.dealRefValueFormatter;
    td.addColumn(tradeIdColumn);

    // Order id
    let orderIdColumn: BfxColDef = this.initColdDef();
    orderIdColumn.headerName = "Order ID";
    orderIdColumn.field = COLUMN_ORDER_ID;
    orderIdColumn.indexType = IndexType.Integer;
    orderIdColumn.minWidth = this.oneWs;
    orderIdColumn.width = BfxConfigIDRefHelper.getOrderRefWs();
    orderIdColumn.columnFilterType = ColumnFilterType.NONE;
    orderIdColumn.valueFormatter = BfxConfigIDRefHelper.orderRefValueFormatter;
    orderIdColumn.getQuickFilterText = BfxConfigIDRefHelper.orderRefValueFormatter;
    if (!this.restoreFromConfig(orderIdColumn)) {
      orderIdColumn.position = this.position.next();
    }
    td.addColumn(orderIdColumn);

    // Strategy = Not in the API
    let strategyColumn: BfxColDef = this.initColdDef();
    strategyColumn.headerName = "Strategy ID";
    strategyColumn.field = COLUMN_STRATEGY_ID;
    strategyColumn.minWidth = this.oneWs;
    strategyColumn.width = BfxConfigIDRefHelper.getStrategyRefWs();
    strategyColumn.indexType = IndexType.Integer;
    strategyColumn.columnFilterType = ColumnFilterType.NONE;
    strategyColumn.valueFormatter = BfxConfigIDRefHelper.strategyRefValueFormatter;
    strategyColumn.getQuickFilterText = BfxConfigIDRefHelper.strategyRefValueFormatter;
    if (!this.restoreFromConfig(strategyColumn)) {
      strategyColumn.hide = true;
      strategyColumn.position = this.position.next();
    }
    td.addColumn(strategyColumn);

    // Client Order ID
    let clOrderIdColumn: BfxColDef = this.initColdDef();
    clOrderIdColumn.headerName = "ClOrderId";
    clOrderIdColumn.field = COLUMN_CLORDERID;
    clOrderIdColumn.minWidth = this.oneWs;
    clOrderIdColumn.width = this.sixWs;
    clOrderIdColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(clOrderIdColumn)) {
      clOrderIdColumn.hide = true;
      clOrderIdColumn.position = this.position.next();
    }
    td.addColumn(clOrderIdColumn);

    // Fixing Code - ndf or benchmarks when enabled
    if (this.myNdfEnabled) {
      let fixingCodeColumn: BfxColDef = this.initColdDef();
      fixingCodeColumn.headerName = "Fixing Code";
      fixingCodeColumn.field = COLUMN_TRADE_FIXING_SOURCE_CODE;
      fixingCodeColumn.alias = [COLUMN_TRADE_FIXING_SOURCE_CODE, COLUMN_ORDER_FIXING_SOURCE_CODE];
      fixingCodeColumn.minWidth = this.oneWs;
      fixingCodeColumn.width = this.sixWs;
      fixingCodeColumn.valueGetter = (params: ValueGetterParams) => {
        let order: CubiqTS.DealImpl = params.data;
        let val = order[COLUMN_TRADE_FIXING_SOURCE_CODE];

        if (val != null && typeof val === "string") {
          return val;
        }

        if (order.productType == "NDF") {
          let desc: string = this._translateService.instant("fixingSource.custom");
          return desc;
        }

        return EMPTY_TEXT_VALUE;
      };
      fixingCodeColumn.columnFilterType = ColumnFilterType.FIXCODE;
      if (!this.restoreFromConfig(fixingCodeColumn)) {
        fixingCodeColumn.hide = !this.myNdfEnabled;
        fixingCodeColumn.position = this.position.next();
      }
      td.addColumn(fixingCodeColumn);

      // Fixing Date
      let fixingDateColumn: BfxColDef = this.initColdDef();
      fixingDateColumn.headerName = "Fixing Date";
      fixingDateColumn.field = COLUMN_FIXING_DATE;
      fixingDateColumn.indexType = IndexType.FixingDate;
      fixingDateColumn.minWidth = this.oneWs;
      fixingDateColumn.width = this.sixWs;
      fixingDateColumn.valueGetter = this.tradeFixingDateValueGetter.bind(this);
      fixingDateColumn.columnFilterType = ColumnFilterType.NONE;
      if (!this.restoreFromConfig(fixingDateColumn)) {
        fixingDateColumn.hide = !this.myNdfEnabled;
        fixingDateColumn.position = this.position.next();
      }
      td.addColumn(fixingDateColumn);
    }

    if (this.myClientTraderEnabled) {
      let clientTraderColumn: BfxColDef = this.initColdDef();
      clientTraderColumn.headerName = "Client Trader";
      clientTraderColumn.field = COLUMN_TRADE_CLIENT_TRADER;
      clientTraderColumn.alias = [COLUMN_CLIENT_TRADER];
      clientTraderColumn.minWidth = this.oneWs;
      clientTraderColumn.width = this.sevenWs;
      clientTraderColumn.columnFilterType = ColumnFilterType.USER;
      clientTraderColumn.columnFilterData = CubiqTS.UserType.CLIENT_TRADER;
      clientTraderColumn.valueGetter = function (params) {
        return this.privatizationHelper.getPrivatizedValue(params, COLUMN_TRADE_CLIENT_TRADER);
      }.bind(this);
      if (!this.restoreFromConfig(clientTraderColumn)) {
        clientTraderColumn.hide = true;
        clientTraderColumn.position = this.position.next();
      }
      td.addColumn(clientTraderColumn);
    }

    // Sales Group
    let salesGroupColumn: BfxColDef = this.initColdDef();
    salesGroupColumn.headerName = "Sales Group";
    salesGroupColumn.field = COLUMN_TRADE_SALES_GROUP_ID;
    salesGroupColumn.alias = [COLUMN_SALES_GROUP_ID];
    salesGroupColumn.minWidth = this.oneWs;
    salesGroupColumn.width = this.sevenWs;
    salesGroupColumn.valueGetter = this.tradeSalesGroupValueGetter.bind(this);
    salesGroupColumn.columnFilterType = ColumnFilterType.SALESGROUP;
    if (!this.restoreFromConfig(salesGroupColumn)) {
      salesGroupColumn.position = this.position.next();
    }
    td.addColumn(salesGroupColumn);

    // client branch
    let clientBranchColumn: BfxColDef = this.initColdDef();
    clientBranchColumn.headerName = "Client Branch";
    clientBranchColumn.field = COLUMN_CLIENT_BRANCH;
    clientBranchColumn.minWidth = this.oneWs;
    clientBranchColumn.width = this.fourWs;
    clientBranchColumn.columnFilterType = ColumnFilterType.BRANCH;
    if (!this.restoreFromConfig(clientBranchColumn)) {
      clientBranchColumn.hide = true;
      clientBranchColumn.position = this.position.next();
    }
    td.addColumn(clientBranchColumn);

    // Entry Date - not available in API
    let entryDateColumn: BfxColDef = this.initColdDef();
    entryDateColumn.headerName = "Entry Date";
    entryDateColumn.field = COLUMN_ENTRY_DATE;
    entryDateColumn.minWidth = this.oneWs;
    entryDateColumn.width = this.sixWs;
    entryDateColumn.indexType = IndexType.Double;
    entryDateColumn.valueFormatter = this.formatDate.bind(this, DATE_LOCALE_FORMAT);
    entryDateColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(entryDateColumn)) {
      entryDateColumn.position = this.position.next();
    }
    td.addColumn(entryDateColumn);

    // Entry username
    let entryUsernameColumn: BfxColDef = this.initColdDef();
    entryUsernameColumn.headerName = "Entry User";
    entryUsernameColumn.field = COLUMN_ENTRY_USER_ID;
    entryUsernameColumn.minWidth = this.oneWs;
    entryUsernameColumn.width = this.sixWs;
    entryUsernameColumn.valueGetter = this.tradeUserNameValueGetter.bind(this, "entryUserId");
    entryUsernameColumn.valueFormatter = this.tradeUserNameValueFormatter.bind(this, "entryUserId");
    entryUsernameColumn.columnFilterType = ColumnFilterType.USER;
    if (!this.restoreFromConfig(entryUsernameColumn)) {
      entryUsernameColumn.position = this.position.next();
    }
    td.addColumn(entryUsernameColumn);

    // Confirmed by
    let confirmUserColumn: BfxColDef = this.initColdDef();
    confirmUserColumn.headerName = "Confirmed by";
    confirmUserColumn.field = COLUMN_CONFIRMED_BY;
    confirmUserColumn.minWidth = this.oneWs;
    confirmUserColumn.width = this.sixWs;
    confirmUserColumn.valueGetter = this.tradeUserNameValueGetter.bind(this, COLUMN_CONFIRMED_BY);
    confirmUserColumn.valueFormatter = this.tradeUserNameValueFormatter.bind(
      this,
      COLUMN_CONFIRMED_BY,
    );
    confirmUserColumn.columnFilterType = ColumnFilterType.USER;
    confirmUserColumn.columnFilterData = CubiqTS.UserType.SALES;
    if (!this.restoreFromConfig(confirmUserColumn)) {
      confirmUserColumn.position = this.position.next();
    }
    td.addColumn(confirmUserColumn);

    // Back Office Ids - not available in API
    let backOfficeColumn: BfxColDef = this.initColdDef();
    backOfficeColumn.headerName = "Back Office IDs";
    backOfficeColumn.field = COLUMN_BACK_OFFICE_IDS;
    backOfficeColumn.minWidth = this.oneWs;
    backOfficeColumn.width = this.sixWs;
    backOfficeColumn.valueFormatter = this.tradeBackOfficeIdsValueGetter.bind(this);
    backOfficeColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(backOfficeColumn)) {
      backOfficeColumn.hide = true;
      backOfficeColumn.position = this.position.next();
    }
    td.addColumn(backOfficeColumn);

    // Client Comment
    let clientCommentColumn: BfxColDef = this.initColdDef();
    clientCommentColumn.headerName = "Client Comments";
    clientCommentColumn.field = COLUMN_CLIENT_COMMENT;
    clientCommentColumn.minWidth = this.oneWs;
    clientCommentColumn.width = this.nineWs;
    clientCommentColumn.showTooltip = true;
    clientCommentColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(clientCommentColumn)) {
      clientCommentColumn.position = this.position.next();
    }
    td.addColumn(clientCommentColumn);

    if (this.myPrivateCommentsEnabled) {
      // Private Comment
      let privateCommentColumn: BfxColDef = this.initColdDef();
      privateCommentColumn.headerName = "Private Comments";
      privateCommentColumn.field = COLUMN_PRIVATE_COMMENT;
      privateCommentColumn.minWidth = this.oneWs;
      privateCommentColumn.width = this.nineWs;
      privateCommentColumn.showTooltip = true;
      privateCommentColumn.valueFormatter = this.formatComment.bind(this, COLUMN_PRIVATE_COMMENT);
      privateCommentColumn.columnFilterType = ColumnFilterType.NONE;
      if (!this.restoreFromConfig(privateCommentColumn)) {
        privateCommentColumn.position = this.position.next();
      }
      td.addColumn(privateCommentColumn);
    }

    // Fill Comment - not available in API
    let fillCommentColumn: BfxColDef = this.initColdDef();
    fillCommentColumn.headerName = "Fill Comments";
    fillCommentColumn.field = COLUMN_FILL_COMMENT;
    fillCommentColumn.minWidth = this.oneWs;
    fillCommentColumn.width = this.nineWs;
    fillCommentColumn.showTooltip = true;
    fillCommentColumn.valueFormatter = this.formatComment.bind(this, COLUMN_FILL_COMMENT);
    fillCommentColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(fillCommentColumn)) {
      fillCommentColumn.position = this.position.next();
    }
    td.addColumn(fillCommentColumn);

    // Status column
    let statusColumn: BfxColDef = this.initColdDef();
    statusColumn.headerName = "Status";
    statusColumn.field = COLUMN_FILL_STATE;
    statusColumn.minWidth = this.oneWs;
    statusColumn.width = this.fiveWs;
    statusColumn.valueGetter = ((params: ValueFormatterParams) => {
      let status: number = (params.data as CubiqTS.DealImpl).fillState;
      let data = "";

      if (status === CubiqTS.FillState.FILLED) {
        return "Filled";
      } else if (status === CubiqTS.FillState.PARTFILLED) {
        return "Part Filled";
      }

      return data;
    }).bind(this);
    statusColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(statusColumn)) {
      statusColumn.hide = true;
      statusColumn.position = this.position.next();
    }
    td.addColumn(statusColumn);

    if (this.enabledOBO) {
      // Reset here for new columns
      this.position.set(50);

      // OBO Client
      let oboClient: BfxColDef = this.initColdDef();
      oboClient.headerName = "OBO Client";
      oboClient.field = COLUMN_OBO_CLIENT;
      oboClient.minWidth = this.oneWs;
      oboClient.width = this.sevenWs;
      oboClient.columnFilterType = ColumnFilterType.NONE;
      oboClient.valueFormatter = (params: ValueFormatterParams) => {
        return params.value == null ? "-" : params.value;
      };

      if (!this.restoreFromConfig(oboClient)) {
        oboClient.hide = true;
        oboClient.position = this.position.next();
      }

      td.addColumn(oboClient);

      // OBO Account
      let oboAccount: BfxColDef = this.initColdDef();
      oboAccount.headerName = "OBO Account";
      oboAccount.field = COLUMN_OBO_ACCOUNT;
      oboAccount.minWidth = this.oneWs;
      oboAccount.width = this.sevenWs;
      oboAccount.columnFilterType = ColumnFilterType.NONE;
      oboAccount.valueFormatter = (params: ValueFormatterParams) => {
        return params.value == null ? "-" : params.value;
      };

      if (!this.restoreFromConfig(oboAccount)) {
        oboAccount.hide = true;
        oboAccount.position = this.position.next();
      }

      td.addColumn(oboAccount);

      let oboEntryUser: BfxColDef = this.initColdDef();
      oboEntryUser.headerName = "OBO Entry User";
      oboEntryUser.field = COLUMN_OBO_ENTRY_USER;
      oboEntryUser.minWidth = this.oneWs;
      oboEntryUser.width = this.sevenWs;
      oboEntryUser.columnFilterType = ColumnFilterType.NONE;
      oboEntryUser.valueFormatter = (params: ValueFormatterParams) => {
        return params.value == null ? "-" : params.value;
      };

      if (!this.restoreFromConfig(oboEntryUser)) {
        oboEntryUser.hide = true;
        oboEntryUser.position = this.position.next();
      }

      td.addColumn(oboEntryUser);
    }

    // Last Market
    let lastMktColumn: BfxColDef = this.initColdDef();
    lastMktColumn.headerName = "Last Market";
    lastMktColumn.field = COLUMN_LAST_MARKET;
    lastMktColumn.minWidth = this.oneWs;
    lastMktColumn.width = this.nineWs;
    lastMktColumn.showTooltip = true;
    lastMktColumn.visibleInEmbeddedMode = true;
    lastMktColumn.valueFormatter = this.formatComment.bind(this, COLUMN_LAST_MARKET);
    lastMktColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(lastMktColumn)) {
      lastMktColumn.hide = true;
      lastMktColumn.position = this.position.next();
    }
    td.addColumn(lastMktColumn);

    if (this.enablePortfolio) {
      // Netting ID (Portfolio ID) Col
      const nettingId: BfxColDef = this.initColdDef();
      nettingId.headerName = "Spot Portfolio";
      nettingId.field = COLUMN_SPOT_PORTFOLIO;
      nettingId.minWidth = this.oneWs;
      nettingId.width = this.sevenWs;
      nettingId.columnFilterType = ColumnFilterType.NONE;
      nettingId.valueFormatter = (params: ValueFormatterParams) => {
        return params.value == null ? "-" : params.value;
      };

      if (!this.restoreFromConfig(nettingId)) {
        nettingId.hide = true;
        nettingId.position = this.position.next();
      }
      td.addColumn(nettingId);

      // Netting ID (Portfolio ID) Col
      const fwdNettingId: BfxColDef = this.initColdDef();
      fwdNettingId.headerName = "Forward Portfolio";
      fwdNettingId.field = COLUMN_FWD_PORTFOLIO;
      fwdNettingId.minWidth = this.oneWs;
      fwdNettingId.width = this.sevenWs;
      fwdNettingId.columnFilterType = ColumnFilterType.NONE;
      fwdNettingId.valueFormatter = (params: ValueFormatterParams) => {
        return params.value == null ? "-" : params.value;
      };

      if (!this.restoreFromConfig(fwdNettingId)) {
        fwdNettingId.hide = true;
        fwdNettingId.position = this.position.next();
      }
      td.addColumn(fwdNettingId);
    }

    // Reset here for new columns
    this.position.set(80);

    // Custom fields
    let custFieldDefns: Array<CubiqTS.CustomFieldImpl> =
      this.customFieldLookupService.getAllCustomFields();
    custFieldDefns.forEach((item) => {
      if (item.isEnabledFg) {
        let customField: BfxColDef = this.initColdDef();
        customField.headerName = item.displayName;
        customField.colSelectorName = this.limitLength(item.displayName, 10);
        customField.field = item.fieldId;
        customField.minWidth = this.sevenWs;
        customField.width = this.oneWs;
        customField.columnFilterType = ColumnFilterType.NONE;
        customField.valueGetter = function (params) {
          let order: CubiqTS.OrderDataImpl = params.data;
          if (order != null) {
            let customValues: Array<CubiqTS.CustomValueImpl> = order.customValues;
            if (customValues != null) {
              for (let i: number = 0; i < customValues.length; i++) {
                if (customValues[i].fieldId === customField.field) {
                  return customValues[i].value;
                }
              }
            }
          }
          return EMPTY_TEXT_VALUE;
        }.bind(this);

        if (!this.restoreFromConfig(customField)) {
          customField.hide = true;
          customField.position = 70 + this.customFieldLookupService.getCustomFieldOrdinal(item);
          if (customField.position == null) {
            customField.position = this.position.next();
          }
        }

        td.addColumn(customField);
      }
    });

    /***************************************************************************/
    /* Pass-Through Params START */
    /*
     * The code below is valid implementation of pass-through parameters for Intervention blotter,
     * while it was removed from the story OMS-33012, it will be used in the future.
     */
    /*         if (this.passThroughParamsEnabled) {
            let passThroughParameters: BfxColDef = this.initColdDef();
            passThroughParameters.headerName = 'Pass-Through Parameters';
            passThroughParameters.colSelectorName = 'Pass-Through ...';
            passThroughParameters.field = COLUMN_ORDER_PASS_THROUGH_PARAMETERS; 
            //passThroughParameters.sortingOrder = [];
            passThroughParameters.minWidth = this.nineWs;
            passThroughParameters.width = this.twelveWs;
            passThroughParameters.columnFilterType = ColumnFilterType.NONE;
            passThroughParameters.valueGetter = function(this: TradeTableDefinitionFactory, params) {
                let order: CubiqTS.OrderDataImpl = params.data;
                if(order.passthroughParameters == null 
                    || !Array.isArray(order.passthroughParameters)
                    || order.passthroughParameters.length < 1)
                {
                    return EMPTY_TEXT_VALUE;
                }

                let passThroughParametersValues = this.atdlTooltipUiRep.inlineUiRep('Pass-Through Parameters', order.passthroughParameters);
                let value = '';

                if (passThroughParametersValues != null && Array.isArray(passThroughParametersValues)) {
                    for (const [i, passThroughParamItem] of passThroughParametersValues.entries()) {
                        value += `${passThroughParamItem.parameterName}: ${passThroughParamItem.parameterValue}`;

                        if (i < (passThroughParametersValues.length - 1))
                        value += `; `;
                    }
                }
                return value;
            }.bind(this);
            passThroughParameters.tooltipValueGetter =  MultiLineTooltipValueGetter;
            if (!this.restoreFromConfig(passThroughParameters)) {
                passThroughParameters.hide = true;
                passThroughParameters.position = this.position.next();
            }
            this.checkforPriorityAndPrimaryClass(passThroughParameters);
            td.addColumn(passThroughParameters);
        } */
    /* Pass-Through Params END */
    /***************************************************************************/

    // hidden column for quick fill dialog
    // hidden column
    // let privatizationCol: BfxColDef = this.initColdDef();
    //     // Client,Account adapters handle this
    //     new PrivatizationAdapter(this._clientLookup),
    //     'Privatization',
    //     'Privatization',
    //     COLUMN_PRIVATIZATION,
    //     IndexType.None,
    //     ColumnFilterType.NONE,
    //     null,
    //     false,
    //     false
    // );
    // privatizationCol.minWidth = this.sixWs;
    // privatizationCol.width = this.sixWs;
    // privatizationCol.visible = false;
    // privatizationCol.omitFromPicker = true;
    // if (!this.restoreFromConfig(statusColumn)) {
    //     statusColumn.visible = false;
    //     statusColumn.position = 47;
    // }
    // td.addColumn(privatizationCol);

    // Dealt Amount = Fill Amount
    // let truncatedFillAmtColumn = new ColumnDefinition(
    //     new TruncatedAmtCcyAdapter(this._ccyLookupService),
    //     'Truncated Dealt Amt',
    //     'Truncated Dealt Amt',
    //     'truncatedDealtAmount',
    //     IndexType.None,
    //     ColumnFilterType.NONE
    // );
    // truncatedFillAmtColumn.visible = false;
    // truncatedFillAmtColumn.omitFromPicker = true;
    // if (!this.restoreFromConfig(truncatedFillAmtColumn)) {
    //     truncatedFillAmtColumn.position = 48;
    // }

    // td.addColumn(truncatedFillAmtColumn);

    return td;
  }

  protected createAggregateIdCol() {
    // Aggregate Deal Id
    let aggregateColumn: BfxColDef = this.initColdDef();
    aggregateColumn.headerName = "Aggregate Deal ID";
    aggregateColumn.valueGetter = (params) => {
      return params.data.aggregateId;
    };

    aggregateColumn.valueFormatter = BfxConfigIDRefHelper.dealAggregationRefValueFormatter;
    aggregateColumn.getQuickFilterText = BfxConfigIDRefHelper.dealAggregationRefValueFormatter;
    aggregateColumn.field = "aggregateRef";
    aggregateColumn.columnFilterType = ColumnFilterType.NONE;
    aggregateColumn.minWidth = this.oneWs;
    aggregateColumn.width = BfxConfigIDRefHelper.getDealRefWs();
    if (!this.restoreFromConfig(aggregateColumn)) {
      aggregateColumn.hide = true;
      aggregateColumn.position = this.position.next();
    }
    return aggregateColumn;
  }

  public getBlotterType(): BlotterType {
    return BlotterType.TRADE;
  }

  public getAssetClass(): AssetClass {
    return AssetClass.FIX;
  }

  public getVersionIdentifier(): string {
    return TRADE_TABLE_DEFINITION_VER;
  }

  protected tradeUserNameValueGetter(column: string, params: ValueFormatterParams): string {
    let userCode: string = params.data[column];

    if (userCode == null || userCode.length > 0) {
      userCode = EMPTY_TEXT_VALUE;
    } else {
      let value: any = params.data[column];
      let user: CubiqTS.UserImpl =
        value instanceof CubiqTS.Long
          ? this._userLookup.getItem(value as CubiqTS.Long)
          : this._userLookup.getUser(value);
      userCode = user != null ? user.userCode : userCode;
    }

    return userCode;
  }

  protected tradeUserNameValueFormatter(column: string, params: ValueFormatterParams): string {
    let userCode: string = params.data[column];

    if (userCode == null || userCode.length > 0) {
      userCode = EMPTY_TEXT_VALUE;
    } else {
      let value: any = params.data[column];
      let user: CubiqTS.UserImpl =
        value instanceof CubiqTS.Long
          ? this._userLookup.getItem(value as CubiqTS.Long)
          : this._userLookup.getUser(value);
      userCode = user != null ? (this.showUserName ? user.userName : user.userCode) : userCode;
    }

    return userCode;
  }

  protected formatDate(format: string, params: ValueFormatterParams): string {
    if (params.value) return params.value.format(format);
    return EMPTY_TEXT_VALUE;
  }

  protected formatUTCDate(format: string, params: ValueFormatterParams): string {
    if (params.value) return params.value.utc().format(format);
    return EMPTY_TEXT_VALUE;
  }

  protected formatInstrument(params: ValueFormatterParams): string {
    if (params.value != null && params.value.length == 6) {
      return params.value.substring(0, 3) + "/" + params.value.substring(3);
    }
    return EMPTY_TEXT_VALUE;
  }

  protected formatCcyAmount(ccy: string, params: ValueFormatterParams): string {
    const detailsBlotter = params.context != null && params.context.embeddedTableMode === true;
    const dp = detailsBlotter ? this.getCcyAmountPrecision(ccy) : 0;

    if (params.value != null && !(params.value as CubiqTS.BigNumber).eq(0)) {
      return (params.value as CubiqTS.BigNumber).toFormat(dp);
    }

    return EMPTY_AMT_RATE_VALUE;
  }

  /**
   * Can use different precision than when formatting amount - currently use full
   * precision always
   * @param ccy
   * @param params
   * @returns
   */
  protected formatCcyAmountTooltip(ccy: string, params: ITooltipParams): string {
    const dp = this.getCcyAmountPrecision(ccy);

    if (params.value != null && !(params.value as CubiqTS.BigNumber).eq(0)) {
      return (params.value as CubiqTS.BigNumber).toFormat(dp);
    }

    return EMPTY_AMT_RATE_VALUE;
  }

  /**
   * Get the minor units precision for the specified currency ID
   * @param currencyId
   * @returns
   */
  protected getCcyAmountPrecision(currencyId: string): number {
    const ccyModel = this.ccyLookupService.getCcy(currencyId);
    let precision: number = 0;

    if (ccyModel != null && ccyModel.minorUnitsQty != null) {
      precision = ccyModel.minorUnitsQty.toNumber();
    }
    return precision;
  }

  protected formatOrderType(params: ValueFormatterParams): string {
    if (params.data[COLUMN_TRADE_ORDER_TYPE] == CubiqTS.RequestOrderType.ALGO) {
      let algoStrategy = this.algoStrategyLookupService.getAlgoStrategy(
        params.data.algoStrategyName,
      );
      return CubiqTS.OrderType.LIMIT_SL == algoStrategy.behaviouralOrderTypeId
        ? '<span class="order-type order-type-sl">' + algoStrategy.description + "</span>"
        : algoStrategy.description;
    } else if (params.data[COLUMN_TRADE_ORDER_TYPE] == CubiqTS.RequestOrderType.SL_ATRATE) {
      return '<span class="order-type order-type-sl">' + SL_ORDER_TYPE + "</span>";
    } else if (params.data[COLUMN_TRADE_ORDER_TYPE] != null) {
      return (
        '<span class="order-type order-type-sl">' +
        CubiqTS.RequestOrderType[params.data[COLUMN_TRADE_ORDER_TYPE]].replace("_", " ") +
        "</span>"
      );
    }
  }

  protected formatCurrency(counter: boolean, params: ValueFormatterParams): string {
    return counter == (params.data.baseCurrencyCd == params.data.dealtCcy)
      ? params.data.nonBaseCurrencyCd
      : params.data.baseCurrencyCd;
  }

  protected tradeTraderGroupValueGetter(params: ValueFormatterParams): string {
    let traderGroup: CubiqTS.TradingGroupImpl = this._traderGroupLookup.getTraderGroup(
      params.data[COLUMN_TRADE_DESK_ID],
    );
    return traderGroup != null ? traderGroup.groupCode : EMPTY_TEXT_VALUE;
  }

  /**
   * please keep available for find trade to subclass
   * @param params ValueFormatterParams from ag-grid
   */
  protected tradeSalesGroupValueGetter(params: ValueFormatterParams): string {
    let salesGroup: CubiqTS.SalesGroupImpl = this._salesGroupLookup.getSalesGroup(
      params.data.salesGroupId,
    );
    return salesGroup != null ? salesGroup.groupCode : EMPTY_TEXT_VALUE;
  }

  protected tradeBackOfficeIdsValueGetter(params: ValueFormatterParams): string {
    let ids: string = EMPTY_TEXT_VALUE;
    if (params.data.bookingIds != null) {
      ids = params.data.bookingIds.join("; ");
    }
    return ids;
  }

  protected formatFillStatus(params: ValueFormatterParams): string {
    let status: number = params["fillState"];

    if (status === CubiqTS.FillState.FILLED) {
      return "Filled";
    } else if (status === CubiqTS.FillState.PARTFILLED) {
      return "Part Filled";
    }

    return EMPTY_TEXT_VALUE;
  }

  protected formatComment(column: string, params: ValueFormatterParams): string {
    return params.data[column] != null ? params.data[column] : EMPTY_TEXT_VALUE;
  }

  protected tradeFixingDateValueGetter(params: ValueFormatterParams): string {
    let order = params.data;
    let fixingDate: string = params.data["fixingDate"];
    params.value = fixingDate;
    if (
      order.requestOrderType != CubiqTS.RequestOrderType.BMARK &&
      order.requestOrderType != CubiqTS.RequestOrderType.BMALGO &&
      order.productType != CubiqTS.Product[CubiqTS.Product.NDF]
    ) {
      return EMPTY_TEXT_VALUE;
    }

    return this.formatUTCDate(DATE_FIXING_FORMAT, params);
  }

  public formatRate(params: ValueFormatterParams) {
    if (params.value == null) {
      return EMPTY_AMT_RATE_VALUE;
    }

    let value: any = params.value;
    if (typeof params.value == "string") {
      value = new CubiqTS.BigNumber(params.value);
    }

    let minPrecision: number = 0;

    let order: CubiqTS.DealImpl = params.data;

    let base: string = order.baseCcyCd;
    let nonBase: string = order.nonBaseCcyCd;
    let dealt: boolean = true;

    let ccyPair: CubiqTS.CurrencyPairImpl = this.ccyPairLookupService.getCcyPair(base, nonBase);

    if (ccyPair) {
      if (order.isInvert && dealt) {
        minPrecision = ccyPair.spotInvertDecimalsQty;
      } else {
        minPrecision = ccyPair.pipsQty;
      }
    }

    let currentDecs: number = value.decimalPlaces();
    if (currentDecs < minPrecision) {
      return value.toFixed(minPrecision);
    }

    return value.toFixed(currentDecs);
  }

  public formatMarkup(params: ValueFormatterParams) {
    if (params.value == null) {
      return EMPTY_AMT_RATE_VALUE;
    }

    let value: any = params.value;
    if (typeof params.value == "string") {
      value = new CubiqTS.BigNumber(params.value);
    }

    let minPrecision: number = 0;

    let order: CubiqTS.DealImpl = params.data;

    let base: string = order.baseCcyCd;
    let nonBase: string = order.nonBaseCcyCd;
    let dealt: boolean = true;

    let ccyPair: CubiqTS.CurrencyPairImpl = this.ccyPairLookupService.getCcyPair(base, nonBase);

    if (ccyPair) {
      if (order.isInvert && dealt) {
        minPrecision = ccyPair.markupSptInvDecimals;
      } else {
        minPrecision = ccyPair.pipsQty;
      }
    }

    let currentDecs: number = value.decimalPlaces();
    if (currentDecs < minPrecision) {
      return value.toFixed(minPrecision);
    }

    return value.toFixed(currentDecs);
  }

  public formatPoints(params: ValueFormatterParams): string {
    let points: CubiqTS.BigNumber = params.value;
    let value: string = EMPTY_TEXT_VALUE;
    let deal: CubiqTS.DealImpl = params.data;

    if (points != null && deal != null && deal.baseCcyCd != null && deal.nonBaseCcyCd != null) {
      let ccyPair: CubiqTS.CurrencyPairImpl = this.ccyPairLookupService.getCcyPair(
        deal.baseCcyCd,
        deal.nonBaseCcyCd,
      );

      if (ccyPair == null) {
        return value;
      }

      points = new CubiqTS.BigNumber(params.value);
      let minPrecision: number = deal.isInvert ? ccyPair.spotInvertDecimalsQty : ccyPair.pipsQty;
      let currentDecs: number = points.decimalPlaces();

      if (currentDecs < minPrecision) {
        value = points.toFixed(minPrecision);
      } else {
        value = points.toFixed(currentDecs);
      }
    }

    return value;
  }

  async getBmarkOrderDisplayName() {
    this.bmarkShortNm = await this.getBMShortNameFromSysConfig();
  }

  private getBMShortNameFromSysConfig() {
    if (this._sysConfigService) {
      return new Promise<string>((res, rej) => {
        this._sysConfigService
          .getSystemConfig(SysFeatures.fixingOrderShortName)
          .then((value) => {
            let returnValue = value != null && value.length > 0 ? value : BMARK_DEFAULT_NAME;
            res(returnValue);
          })
          .catch((error) => {
            logger.out.error(`TradeTableDefinitionFactory : getSystemConfig :
                        failed to get system config for: ${SysFeatures.fixingOrderShortName}`);
            rej(error.errorMessages);
          });
      });
    }
  }

  private getStatusCellClassRules() {}
}
